import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/components/axiosTokenConfig";

// useSrfDetails hook
const formatDate = (date) => {
  if (!date) return null;
  let Newdate = moment(date).format("YYYY-MM-DD");
  return new Date(Newdate);
};

export const useSrfDetails = (initialState) => {
  const [srfDetails, setSrfDetails] = useState(initialState);

  const fetchSrfDetails = async (id) => {
    try {
      const res = await axiosWithToken.get(
        `${BASE_URL}xjoin?_join=srf.srfs,_j,client.clients&_on1=(srf.clientId,eq,client.id)&_fields=srf.serviceReqNumber,srf.companyHierarchy,srf.jobNumber,srf.customerAnnextureFilePaths,srf.ducImages,srf.poNumber,srf.contactPersonDetails,srf.calType,srf.poDate,srf.billingId,srf.receiptDate,srf.committedDeliveryDate,srf.dcNumber,srf.dcDate,srf.entryDate,srf.description,srf.address,srf.billingAddress,srf.reservedSrf,client.address,client.id,client.companyName&_where=(srf.id,eq,${id})`
      );
      const data = res.data[0];
      const _srfObject = {
        id: Number(id || 0),
        clientId: data.client_id,
        serviceReqNumber: data?.srf_serviceReqNumber,
        companyHierarchy: data?.srf_companyHierarchy,
        jobNumber: data?.srf_jobNumber,
        poNumber: data?.srf_poNumber,
        calType: Number(data?.srf_calType),
        poDate: formatDate(data.srf_poDate),
        receiptDate: formatDate(data.srf_receiptDate),
        committedDeliveryDate: formatDate(data.srf_committedDeliveryDate),
        dcNumber: data.srf_dcNumber,
        reservedSrf: data.srf_reservedSrf,
        dcDate: formatDate(data.srf_dcDate),
        entryDate: formatDate(data.srf_entryDate),
        description: data.srf_description,
        address: data.srf_address,
        contactPersonDetails: JSON.parse(data.srf_contactPersonDetails),
        companyName: data.client_companyName,
        billingId: data?.srf_billingId,
        billingAddress: data?.srf_billingAddress,
        customerAnnextureFilePaths:
          data?.srf_customerAnnextureFilePaths?.split(",") || [],
        ducImages: data?.srf_ducImages?.split(",") || [],
      };
      setSrfDetails(_srfObject);
      sessionStorage.setItem("oldSrfDetails", JSON.stringify(_srfObject));
    } catch (err) {
      console.error("Something Went Wrong at (fetchSrfDetails) : ", err);
    }
  };

  const updateSrfDetails = (details) => {
    setSrfDetails((prevState) => ({ ...prevState, ...details }));
  };

  return [srfDetails, updateSrfDetails, fetchSrfDetails];
};

export const fetchDescriptionRows = async (id, dispatch) => {
  try {
    dispatch({
      field: "loadingDUCInward",
      value: true,
    });
    let url = BASE_URL;
    const [instrumentsResponse, settingsResponse] = await Promise.all([
      axiosWithToken.get(
        `${url}srfInstruments?_where=(srfsId,eq,${id})~and(status,eq,1)`
      ),
      axiosWithToken.get(
        `${url}settings?_where=((keyName,eq,srfInstrumentsAdditionalColumns)~and(status,eq,1))`
      ),
    ]);

    const instrumentsData = instrumentsResponse?.data || [];
    const extraColumnFromSetting = settingsResponse?.data?.[0]?.value;
    const extraColumnsKeys = extraColumnFromSetting?.split(",") || [];

    let rows = instrumentsData.map((row) => {
      let extraData = row?.extraColumns && JSON.parse(row?.extraColumns || {});

      const newRow = {
        id: row.id,
        instrumentId: Number(row.instrumentId),
        requestedDucName: row.requestedDucName,
        DUCID: row.DUCID,
        serialNumber: row.serialNumber,
        ranges: row.ranges,
        lc: row.lc,
        make: row.make,
        model: row.model,
        department: row.department,
        operatingRange: row.operatingRange,
        ConOfDuc: row.ConOfDuc,
        calFrequency: row.calFrequency,
        accuracy: row.accuracy,
        calibrationType: row.calibrationType,
        location: row.location,
        locationOfInstrument: row.locationOfInstrument,
        calPoint: row.calPoint,
        calMethod: row.calMethod,
        materialType: row.materialType,
        sensorType: row.sensorType,
        sensorCoefficient: row.sensorCoefficient ? row.sensorCoefficient : "",
        labCapabilities: row.labCapabilities,
        mpe: row.mpe,
        extraColumns: {},
      };

      extraColumnsKeys.forEach((key) => {
        newRow.extraColumns[key] = extraData[key] || "";
      });

      return newRow;
    });

    dispatch({
      field: "ducInwardRows",
      value: [...rows],
    });
    const oldRowsDucInward = JSON.parse(
      JSON.stringify(
        rows.map((row) => ({
          ...row,
          extraColumns: JSON.stringify(row.extraColumns),
        }))
      )
    );

    dispatch({
      field: "ducInwardOriginalRows",
      value: [...oldRowsDucInward],
    });
    sessionStorage.setItem("oldSrfInstruments", JSON.stringify(rows));
    dispatch({
      field: "loadingDUCInward",
      value: false,
    });
  } catch (error) {
    dispatch({
      field: "loadingDUCInward",
      value: false,
    });
    console.error("Error while fetching or processing data: ", error);
    if (error.message !== "request_aborted") {
      toast.error("Error while fetching or processing data");
    }
  }
};

// useInstruments hook
const initiateInstrumentList = (instrumentArray) => {
  var pushIArray = [];
  for (let i = 0; i < instrumentArray.length; i++) {
    if (instrumentArray[i].instrumentName) {
      pushIArray.push({
        label: instrumentArray[i].id + ", " + instrumentArray[i].instrumentName,
        id: instrumentArray[i].id,
        discipline: instrumentArray[i].name,
        calibrationProcedureNo: instrumentArray[i].calibrationProcedureNo,
      });
    }
  }
  return pushIArray;
};

export const useInstruments = (initialState) => {
  const [instrumentArray, setInstrumentArray] = useState(initialState);
  const [instrumentList, setInstrumentList] = useState([]);
  const [assignInstrumentIds, setAssignInstrumentIds] = useState("");

  const [userId, setUserId] = useState(localStorage.getItem("id"));
  const [userType, setUserType] = useState(localStorage.getItem("type"));

  const fetchInstrumentsList = (params) => {
    if (params?.filterWithDucMaster) {
      let query = `SELECT instruments.* FROM (SELECT DISTINCT instrumentId FROM ducMaster WHERE clientId = ${
        params?.clientId || 0
      }) AS ducMaster LEFT JOIN instruments ON ducMaster.instrumentId = instruments.id ${
        userType == 2 && assignInstrumentIds
          ? `WHERE instruments.id IN (${assignInstrumentIds})`
          : ""
      }`;

      axiosWithToken
        .post(
          BASE_URL + "dynamic",
          { query: query },
          { headers: { requestType: "fetchData" } }
        )
        .then((res) => {
          setInstrumentArray([...res.data]);
        })
        .catch((error) => {
          if (error.message !== "request_aborted") {
            toast.error("Something Went Wrong!");
          }
        });
    } else {
      axiosWithToken
        .get(
          BASE_URL +
            `xjoin?_join=ins.instruments,_j,d.discipline&_on1=(ins.disciplineId,eq,d.id)&_fields=ins.c1,ins.c2,ins.c3,ins.c4,ins.c5,ins.c6,ins.c7,ins.c8,ins.c9,ins.c10,ins.calProcRefNo,ins.calibrationProcedureNo,ins.cmcMinus,ins.cmcPlus,ins.disciplineId,ins.id,ins.instrumentName,ins.isGauge,ins.lastModified,ins.materialType,ins.referenceStandards,ins.standardMasterIds,ins.status,ins.supportiveInstrumentMasterData,ins.uncertaintyFactors,ins.uncertaintyMasterData,d.name&_where=(ins.status,eq,1)${
              userType == 2 && assignInstrumentIds
                ? `~and(ins.id,in,${assignInstrumentIds})`
                : ""
            }`
        )
        .then((res) => {
          let d = JSON.stringify(res.data)
            .replaceAll("ins_", "")
            .replaceAll("d_", "");
          res.data = JSON.parse(d);
          setInstrumentArray([...res.data]);
        })
        .catch((error) => {
          if (error.message !== "request_aborted") {
            toast.error("Something Went Wrong!");
          }
        });
    }
  };

  const getInstrument = (id) => {
    for (let instrument in instrumentList) {
      if (instrumentList[instrument].id == id)
        return instrumentList[instrument];
    }
    return null;
  };

  const getAssignedInstrumentForEngineer = (userId) => {
    axiosWithToken
      .get(
        BASE_URL + `assignedDucs?_where=(userId,eq,${userId})~and(status,eq,1)`
      )
      .then((res) => {
        setAssignInstrumentIds(res.data?.[0]?.instrumentIds);
      })
      .catch((error) => {
        if (error.message !== "request_aborted") {
          toast.error("Something Went Wrong!");
        }
      });
  };

  useEffect(() => {
    if (userType == 2) getAssignedInstrumentForEngineer(userId);
    setInstrumentList(initiateInstrumentList(instrumentArray));
  }, [userId, instrumentArray, userType]);

  return [instrumentList, instrumentArray, fetchInstrumentsList, getInstrument];
};

// useClient hook
export const fetchClientIdList = (setClientArray) => {
  let url = BASE_URL;
  let userType = localStorage.getItem("type");
  let branch = localStorage.getItem("branch");
  let validClients = localStorage.getItem("validClients");
  const branchWhere = () => {
    if (branch && branch !== "null" && branch !== "undefined")
      return `~and(c.branch,in,${branch})`;
    return "";
  };
  axiosWithToken
    .get(
      url +
        `xjoin?_join=c.clients,_j,b.branch&_on1=(c.branch,eq,b.id)&_fields=c.address,c.branch,c.companyName,c.contact,c.email,c.gstNumber,c.id,c.lastModified,c.matrixId,c.shippingAddress,c.signature,c.state,c.status,c.uniCode,b.branchName&_where=(c.status,eq,1)${
          userType == 1
            ? ""
            : userType == 4
            ? `${branchWhere()}`
            : validClients?.length > 0
            ? `${branchWhere()}~and(c.id,in,${validClients})`
            : `${branchWhere()}`
        }`
    )
    .then((res) => {
      let d = (res.data = JSON.stringify(res.data)
        .replaceAll("c_", "")
        .replaceAll("b_", ""));
      res.data = JSON.parse(d);
      setClientArray(res.data);
    })
    .catch((error) => {
      if (error.message !== "request_aborted") {
        toast.error("Failed to fetch client list");
      }
      console.log({ error });
    });
};

// use unit master hook
export const useUnitMaster = (initialState) => {
  const [unitArray, setUnitArray] = useState(initialState);
  const [unitFilter, setUnitFilter] = useState([]);

  const fetchUnitMaster = () => {
    let url = BASE_URL;
    axiosWithToken
      .get(url + "unit?_where=(status,eq,1)")
      .then((res) => {
        setUnitArray([""].concat(res.data));
      })
      .catch((error) => {
        if (error.message !== "request_aborted") {
          toast.error("Something Went Wrong!");
        }
      });
  };

  const UnitList = () => {
    var uArr = [];
    for (let i = 0; i < unitArray.length; i++) {
      uArr.push({
        label: unitArray[i].id + ", " + unitArray[i].symbol,
      });
    }
    setUnitFilter(uArr);
  };

  useEffect(() => {
    UnitList();
  }, [unitArray]);

  return [unitArray, unitFilter, setUnitFilter, fetchUnitMaster];
};

export const useSettings = (initialState) => {
  const [settings, setSettings] = useState(initialState);

  const fetchSettings = (keys) => {
    keys = keys.map((key) => `(keyName,eq,${key})`).join("~or");
    let url = BASE_URL;
    axiosWithToken
      .get(
        url +
          `settings?_where=(${keys})~and((status,eq,1)~or(status,eq,-2))&_fields=id,keyName,value`
      )
      .then((res) => {
        let data = {};
        res.data.forEach((e) => {
          data[e.keyName] = e;
        });

        setSettings({
          ...settings,
          ...data,
        });
      })
      .catch((error) => {
        if (error.message !== "request_aborted") {
          toast.error("Failed to fetch settings");
        }
      });
  };

  return [settings, fetchSettings];
};

export const fetchSettings = (keysToFetch, settings, dispatch) => {
  keysToFetch = keysToFetch.map((key) => `(keyName,eq,${key})`).join("~or");
  let url = BASE_URL;
  axiosWithToken
    .get(
      url +
        `settings?_where=(${keysToFetch})~and((status,eq,1)~or(status,eq,-2))&_fields=id,keyName,value`
    )
    .then((res) => {
      let data = {};
      res.data.forEach((e) => {
        data[e.keyName] = e;
      });

      dispatch({
        field: "settings",
        value: {
          ...settings,
          ...data,
        },
      });
    })
    .catch((error) => {
      if (error.message !== "request_aborted") {
        toast.error("Failed to fetch settings");
      }
    });
};

export const fetchCSRFFormData = async (id) => {
  let csrfFormFetch = {};
  await axiosWithToken
    .get(BASE_URL + `csrfForm?_where=(srfId,eq,${id})`)
    .then((res) => {
      let resDatas = res?.data?.[0];
      if (resDatas) {
        csrfFormFetch = resDatas;
      }
    })
    .catch((error) => {
      console.log("Something Went Wrong!");
    });

  return csrfFormFetch;
};

export const generateSRFRange = (rangeString, srfDetails) => {
  let result = [];
  let ranges = rangeString?.split(",");

  if (srfDetails?.calType != 1) return;

  ranges?.forEach((range) => {
    let [start, end] = range?.split("-").map(Number);
    for (let i = start; i <= end; i++) {
      // Extract prefix from serviceReqNumber
      const parts = srfDetails?.serviceReqNumber?.split("/") || [];
      const prefix = parts?.slice(0, -1)?.join("/");
      result.push(prefix ? `${prefix}/${i}` : i);
    }
  });
  return result;
};

export const fetchSRFList = async () => {
  let srfList = {};
  await axiosWithToken
    .post(BASE_URL + `dynamic`,{
      query : "select id, serviceReqNumber, calType from srfs where status = 1 and calType = 1"
    })
    .then((res) => {
        srfList = res.data;
    })
    .catch((error) => {
      console.log("Something Went Wrong!");
    });
  return srfList;
};

export const fetchReservefromandto = async () => {
  let srfList = [];
  await axiosWithToken
    .post(BASE_URL + `dynamic`, {
      query:
        "SELECT * from reservedSrfSeries where status = 1 ORDER BY id DESC;",
    })
    .then((res) => {
      srfList = res.data;
    })
    .catch((error) => {
      console.log("Something Went Wrong!");
    });
  return srfList;
};
